// UTIL reference: https://technicalseo.com/tools/schema-markup-generator/

import React /*, { useEffect, useState, useCallback, useRef }*/ from 'react';

import css from './styles.module.scss';
import { ensureURL } from '@/utils/functions';

/**
 * React component: JsonLD
 *
 * Adds available JSON-LD scripts
 *
 * @param {Object} props Component's properties
 * @return {ReactComponent} JsonLD
 */
export default function JsonLD(props) {

  const {
    className = '',
    children,
    type,
    domain,
    data = {},
    ...other
  } = props;

  const renderers = {
    'org': ldOrg,
    'site': ldSite,
    'organization': ldOrg,
    'breadcrumb': ldBC,
    'video': ldVideo,
    'faq': ldFAQ,
  };

  const func = renderers[data.type || type];

  return (
    <script
      type="application/ld+json"
      // key={'schemaGeral'}
      dangerouslySetInnerHTML={{
        __html: func(data, { domain, type })
      }}
    />
  );
}

/*
{
  "@type": "ContactPoint",
  "telephone": "123123",
  "contactType": "technical support", <-----
  "contactOption": ["TollFree", "HearingImpairedSupported"],
  "areaServed": ["BR", "AR"],
  "availableLanguage": ["en", "Portuguese"]
}

contactType accepted:
  - technical support
  - customer service
  - billing support
  - bill payment
  - sales
  - reservations
  - credit card support
  - emergency
  - baggage tracking
  - roadside assistance
  - package tracking
*/

function ldOrg(data) {
  return `
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "${data.name}",
      "url": "${ensureURL(data.url) || ''}"
      "logo": "${data.logo || ''}",
      "contactPoint": [
      ],
      "sameAs": [
        "${data.socialNetworks
          ?.map(sn => sn.value)
          ?.join('",\n        "')
        }"
      ]
    }
  `;
}

function ldBC (data, options = {}) {
  return `
    {
      "@context": "https://schema.org/", 
      "@type": "BreadcrumbList", 
      "itemListElement": [
        ${
          data.map((bc, i) => {
            return `
        {
          "@type": "ListItem",
          "position": ${i + 1},
          "name": "${bc.title}",
          "item": "${ensureURL(options?.domain || '', bc.path)}"
        },`;
          })
        }
      ]
    }
  `;
}

function ldSite (data, options) {
  return `
    {
      "@context": "https://schema.org/",
      "@type": "WebSite",
      "name": "${data.title}",
      "url": "${ensureURL(options.domain)}",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "${ensureURL(options.domain)}search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    }
  `;
}

function ldVideo (data, options) {
  return `
    {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      "name": "${data.title}",
      "description": "${data.description}",
      "thumbnailUrl": "${data.thumb || data.poster || data.image?.public_url || data.image}",
      "uploadDate": "${data.date}",
      "duration": "PT${minutes}M1${secs}",
      "contentUrl": "${data.page}",
      "embedUrl": "${data.url}"
    }
  `;
}

function ldFAQ (data, options) {
  return `
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        ${
          data.questions.map(q => {
            return `
          {
            "@type": "Question",
            "name": "${q.question}",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "${q.answer}"
            }
          }
        `;
          }).join(",\n          ")
        }
      ]
    }
  `;
}

function ldArticle (data, options) {
  return `
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "${data.url}"
      },
      "headline": "${data.title}",
      "description": "${data.description}",
      "image": "${data.image?.public_url || data.image || ''}",
      "author": {
        "@type": "${data.author?.type || 'Organization'}",
        "name": "${data.author?.name || options.title}",
        "url": "${data.author?.url || ''}"
      },
      "publisher": {
        "@type": "Organization",
        "name": "${options.title}",
        "logo": {
          "@type": "ImageObject",
          "url": "${options.logo}"
        }
      },
      "datePublished": "${data.datePublished || data.date || ''}"
    }
  `;
}

function ldProduct (data, options) {
  return `
    {
      "@context": "https://schema.org/", 
      "@type": "Product", 
      "name": "${data.name}",
      "image": "${data.image?.public_url || data.image || ''}",
      "description": "${data.description || ''}",
      "brand": {
        "@type": "Brand",
        "name": "${data.brand || ''}"
      },
      "sku": "${data.sku || ''}",
      "offers": {
        "@type": "Offer",
        "url": "${data.offer?.url}",
        "priceCurrency": "${data.currency || options.currency}",
        "price": "${
          data.offer?.price
            ? parseFloat(data.offer?.price)
            : ''
          }",
        "priceValidUntil": "${
          // format 2025-01-21
          data.offer.validity || ''
        }",
        "availability": "https://schema.org/${
          // InStock
          // OutOfStock
          // OnlineOnly
          // InStoreOnly
          // PreOrder
          // PreSale
          // LimitedAvailability
          // SoldOut
          // Discontinued
          data.availability || ''
        }",
        "itemCondition": "https://schema.org/${
          // New
          // Used
          // Refurbished
          // Damaged
          data.condition || ''
        }Condition"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "9",
        "bestRating": "10",
        "worstRating": "0",
        "ratingCount": "10000"
      }
    }
  `;
}
